// 파일 전송(설계서 §9.4) — "file" DataChannel 하나를 감싼다(에이전트가 만든 채널을 ondatachannel 로 받는다).
//
// - send(source, dest): 내 파일을 원격으로(Ctrl+V·끌어다 놓기). offer → accept(이어받기 위치) → 조각·해시 → done → result.
// - pull(copy): 원격 파일을 가져오기 위한 제안을 받는다(Ctrl+C). 제안을 받을지·어디에 둘지는 호출측이 정한다(`receive`).
// 전송은 **한 줄로** 세운다 — 조각이 섞이지 않고, 사람에게 보이는 진행도 한 건씩 흐른다.
import {
  CHUNK_BYTES,
  PROGRESS_EVERY_BYTES,
  RECV_WINDOW_BYTES,
  FILE_ERR,
  FILE_MSG,
  FileError,
  checkEntries,
  decodeFrame,
  encodeFrame,
  makeOffer,
  newTransferId,
  offerJson,
  parseFileHello,
  parseListing,
  parseOffer,
  type Dest,
  type FileCaps,
  type Listing,
  type Offer,
} from './protocol';
import { Sha256 } from './sha256';
import type { FileSink } from './sink';
import type { FileSource } from './source';

/** 보낼 파일을 한 번에 읽는 기본 크기(조각 CHUNK_BYTES 여러 개). 출처가 `blockSize` 로 더 큰 값을 원하면 그것을 쓴다. */
const READ_BLOCK = 1024 * 1024;
/** 보내기 대기열 상한 — 넘으면 비워질 때까지 기다린다. */
const HIGH_WATER = 4 * 1024 * 1024;
const LOW_WATER = 1024 * 1024;
/** 제안에 대한 답(accept/reject). 원격 에이전트는 사용자 권한 스레드를 띄우고 디스크를 확인한 뒤 답한다. */
const ACCEPT_TIMEOUT_MS = 60_000;
/** done 뒤 결과(해시 마무리·붙여넣기). */
const RESULT_TIMEOUT_MS = 120_000;
/** pull 뒤 제안이 오기까지(복사 대기 1.5초 + 목록 만들기). */
const PULL_TIMEOUT_MS = 60_000;
/** 파일 패널 명령(목록·새 폴더·삭제) — 원격이 사용자 권한 스레드를 띄우고 폴더를 읽는 시간. */
const PANEL_TIMEOUT_MS = 30_000;

/** 취소 표시(사용자가 [취소]를 누르면 true 로 바꾼다). */
export interface AbortFlag {
  aborted: boolean;
}

export interface Progress {
  /** 보냈거나 받은 바이트. */
  done: number;
  total: number;
}

type Listener = (v: Record<string, unknown>) => void;

/** 가져오기 요청의 결과. */
export type PullResult =
  | { kind: 'offer'; offer: Offer }
  | { kind: 'unchanged' }
  | { kind: 'not_files' };

/** 받는 중인 전송(에이전트→뷰어). */
interface Incoming {
  offer: Offer;
  sink: FileSink;
  onProgress?: (p: Progress) => void;
  received: number;
  /** 파일별 쓴 바이트·해시. */
  written: Map<number, number>;
  hashers: Map<number, Sha256>;
  /** 조각·fend 를 순서대로 처리하는 사슬. */
  chain: Promise<void>;
  failed: FileError | null;
  finish(error?: FileError): void;
}

export class FileChannel {
  private caps: FileCaps | null = null;
  private queue: Promise<unknown> = Promise.resolve();
  /** 전송 x → JSON 응답 받는 곳. */
  private readonly listeners = new Map<number, Listener>();
  private readonly incoming = new Map<number, Incoming>();

  /**
   * @param dc     에이전트가 만든 file 채널
   * @param onCaps 에이전트 hello 수신 — 상위가 파일 기능을 켠다
   */
  constructor(
    private readonly dc: RTCDataChannel,
    private readonly onCaps: (caps: FileCaps) => void,
  ) {
    dc.binaryType = 'arraybuffer';
    dc.bufferedAmountLowThreshold = LOW_WATER;
    dc.onmessage = (ev) => this.onMessage(ev.data);
    dc.onclose = () => this.failAll();
  }

  get capabilities(): FileCaps | null {
    return this.caps;
  }

  has(feature: FileCaps['features'][number]): boolean {
    return !!this.caps?.features.includes(feature);
  }

  /**
   * 내 파일을 원격으로 보낸다. 끝나면(원격이 붙여넣기까지 마치면) 풀린다.
   * @param abort 사용자가 [취소]를 누르면 `aborted=true` — 다음 조각 전에 멈추고 상대에게 취소를 알린다.
   */
  send(source: FileSource, dest: Dest, onProgress?: (p: Progress) => void, abort?: AbortFlag): Promise<void> {
    return this.enqueue(async () => {
      const caps = this.requireOpen();
      checkEntries(source.entries, caps);
      const x = newTransferId();
      const offer = makeOffer(x, source.entries, dest);
      const replies = this.listen(x);
      try {
        this.dc.send(offerJson(offer));
        const first = await replies.next(ACCEPT_TIMEOUT_MS, [FILE_MSG.ACCEPT, FILE_MSG.REJECT]);
        if (first.t === FILE_MSG.REJECT) {
          throw new FileError(String(first.err ?? FILE_ERR.FAILED));
        }
        const resume = new Map<number, number>();
        for (const r of Array.isArray(first.resume) ? first.resume : []) {
          const o = r as { i?: unknown; offset?: unknown };
          if (typeof o.i === 'number' && typeof o.offset === 'number') {
            resume.set(o.i, o.offset);
          }
        }
        let sent = [...resume.values()].reduce((a, b) => a + b, 0);
        onProgress?.({ done: sent, total: offer.total });
        for (const e of offer.entries) {
          if (e.dir) {
            continue;
          }
          const from = Math.min(resume.get(e.i) ?? 0, e.size);
          const hasher = new Sha256();
          const block = source.blockSize ?? READ_BLOCK;
          // 이어받기면 앞부분은 보내지 않고 해시에만 먹인다(받는 쪽이 전체 해시로 확인한다).
          for (let off = 0; off < from; off += block) {
            hasher.update(await source.read(e.i, off, Math.min(block, from - off)));
          }
          // 크게 읽고(READ_BLOCK) 작게 보낸다(CHUNK_BYTES) — 32KB 마다 비동기 읽기를 하면 그 부담이 전송을 좌우한다
          // (1차 측정: 64MB 가 루프백에서 20초 = 3.1MB/s).
          for (let off = from; off < e.size; ) {
            if (abort?.aborted) {
              throw new FileError(FILE_ERR.CANCELED);
            }
            replies.throwIfFailed();
            const data = await source.read(e.i, off, Math.min(block, e.size - off));
            if (data.length === 0) {
              throw new FileError(FILE_ERR.LOCAL_READ, '파일이 짧아졌다');
            }
            hasher.update(data);
            for (let p = 0; p < data.length; p += CHUNK_BYTES) {
              await this.flow();
              this.dc.send(encodeFrame(x, e.i, off + p, data.subarray(p, Math.min(p + CHUNK_BYTES, data.length))));
            }
            off += data.length;
            sent += data.length;
            onProgress?.({ done: sent, total: offer.total });
          }
          // 이어받기 위치가 크기와 같아도 해시는 보낸다(받는 쪽이 디스크 파일을 확인하고 끝낸다).
          this.dc.send(JSON.stringify({ t: FILE_MSG.FEND, x, i: e.i, sha256: hasher.digestHex() }));
        }
        this.dc.send(JSON.stringify({ t: FILE_MSG.DONE, x }));
        const result = await replies.next(RESULT_TIMEOUT_MS, [FILE_MSG.RESULT]);
        if (result.ok !== true) {
          throw new FileError(String(result.err ?? FILE_ERR.FAILED));
        }
      } catch (e) {
        const fe = e instanceof FileError ? e : new FileError(FILE_ERR.FAILED, String(e));
        // 우리 쪽에서 멈췄으면 상대에게도 알린다(상대가 만든 부분 파일을 지우도록).
        if (fe.code !== FILE_ERR.CLOSED && this.dc.readyState === 'open') {
          this.dc.send(JSON.stringify({ t: FILE_MSG.CANCEL, x }));
        }
        throw fe;
      } finally {
        replies.close();
      }
    });
  }

  /**
   * 원격 파일 가져오기 요청. copy=true(Ctrl+C)면 에이전트가 복사 단축키를 누르고 바뀐 경우에만 제안한다.
   * 제안이 오면 **받을지·어디에 둘지 정해 `receive` 또는 `decline` 을 불러야 한다**.
   */
  pull(copy: boolean): Promise<PullResult> {
    return this.enqueue(async () => {
      this.requireOpen();
      const x = newTransferId();
      const replies = this.listen(x);
      try {
        this.dc.send(JSON.stringify({ t: FILE_MSG.PULL, x, copy }));
        // 에이전트는 자기 id 로 새 제안을 만든다 — pull 의 x 로 오는 것은 거절뿐이다. 제안은 onMessage 가 pendingOffer 로 넘긴다.
        const v = await this.rejectOrOffer(replies);
        if (v.t === FILE_MSG.REJECT) {
          if (v.err === FILE_ERR.UNCHANGED) return { kind: 'unchanged' };
          if (v.err === FILE_ERR.NOT_FILES) return { kind: 'not_files' };
          throw new FileError(String(v.err ?? FILE_ERR.FAILED));
        }
        return { kind: 'offer', offer: parseOffer(v) };
      } finally {
        replies.close();
      }
    });
  }

  /**
   * 끌어내기(F5, 통합앱) — 원격에서 끌던 것을 **영상 밖으로** 냈다.
   * 에이전트는 원격의 끌기를 Esc 로 없던 일로 만들고 **끌던 선택 항목**을 제안한다(`pull` 과 같은 모양).
   * 끌던 것이 파일이 아니었으면 `not_files` — 호출측은 조용히 넘어간다.
   *
   * `nx`·`ny` = 영상을 벗어난 지점(규약 §9.4). 지금 에이전트는 쓰지 않지만 규약대로 보낸다.
   */
  dragout(nx: number, ny: number): Promise<PullResult> {
    return this.enqueue(async () => {
      this.requireOpen();
      const x = newTransferId();
      const replies = this.listen(x);
      try {
        this.dc.send(JSON.stringify({ t: FILE_MSG.DRAGOUT, x, nx, ny }));
        // pull 과 마찬가지로 제안은 에이전트 자기 id 로 온다 — 이 x 로 오는 것은 거절뿐이다.
        const v = await this.rejectOrOffer(replies);
        if (v.t === FILE_MSG.REJECT) {
          if (v.err === FILE_ERR.NOT_FILES) {
            return { kind: 'not_files' };
          }
          throw new FileError(String(v.err ?? FILE_ERR.FAILED));
        }
        return { kind: 'offer', offer: parseOffer(v) };
      } finally {
        replies.close();
      }
    });
  }

  /** 받은 제안을 수락하고 `sink` 에 받는다. 끝나면 풀린다. */
  receive(offer: Offer, sink: FileSink, onProgress?: (p: Progress) => void): Promise<void> {
    return this.enqueue(async () => {
      this.requireOpen();
      const x = offer.x;
      await sink.begin(offer.entries);
      await new Promise<void>((resolve, reject) => {
        const inc: Incoming = {
          offer,
          sink,
          onProgress,
          received: 0,
          written: new Map(),
          hashers: new Map(),
          chain: Promise.resolve(),
          failed: null,
          finish: (error) => {
            this.incoming.delete(x);
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          },
        };
        this.incoming.set(x, inc);
        // 뷰어 쪽 이어받기는 아직 없다 — 늘 처음부터(resume 빈 목록).
        // win = 받기 창: 보내는 쪽은 우리가 prog 로 알린 위치보다 이만큼 이상 앞서 보내지 않는다(저장이 느려도 메모리에 쌓이지 않게).
        this.dc.send(JSON.stringify({ t: FILE_MSG.ACCEPT, x, resume: [], win: RECV_WINDOW_BYTES }));
      }).catch(async (e) => {
        await sink.abort();
        throw e;
      });
    });
  }

  /** 받는 중인 전송을 취소한다(사용자 [취소]) — 상대는 보내기를 멈추고, 우리 쪽은 만든 것을 지운다. */
  cancelReceive(x: number): void {
    const inc = this.incoming.get(x);
    if (inc) {
      this.failIncoming(inc, new FileError(FILE_ERR.CANCELED));
    }
  }

  /** 받은 제안을 거절한다(사용자가 [저장]을 누르지 않았거나 나중에 다시 가져오기로 했다). */
  decline(offer: Offer): void {
    if (this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify({ t: FILE_MSG.REJECT, x: offer.x, err: FILE_ERR.CANCELED }));
    }
  }

  // ── 파일 패널(설계서 §9.4 "파일 패널") ──────────────────────────────────

  /**
   * 패널 명령 한 건. 🔴 전송 대기열(`enqueue`)을 **타지 않는다** — 10GB 전송이 도는 동안 목록이 그 뒤에 줄을 서면
   * 패널이 멈춘 것처럼 보인다. 패널 명령은 짧고 서로 독립이라 그대로 내보낸다(응답은 전송 id 로 갈라진다).
   */
  private async panelCall(
    body: Record<string, unknown>,
    want: string[],
    timeoutMs = PANEL_TIMEOUT_MS,
  ): Promise<Record<string, unknown>> {
    this.requireOpen();
    const x = newTransferId();
    const replies = this.listen(x);
    try {
      this.dc.send(JSON.stringify({ ...body, x }));
      const v = await replies.next(timeoutMs, [...want, FILE_MSG.REJECT]);
      if (v.t === FILE_MSG.REJECT) {
        throw new FileError(String(v.err ?? FILE_ERR.FAILED));
      }
      return v;
    } finally {
      replies.close();
    }
  }

  /** 원격 폴더 목록. `dir` 이 비면 최상위(드라이브·기기가 허용한 루트). */
  async list(dir: string): Promise<Listing> {
    return parseListing(await this.panelCall({ t: FILE_MSG.LIST, dir }, [FILE_MSG.LISTING]));
  }

  /** 원격에 새 폴더(같은 이름이 있으면 `exists` 로 실패 — 덮어쓰지 않는다). */
  async mkdir(dir: string, name: string): Promise<void> {
    await this.panelCall({ t: FILE_MSG.MKDIR, dir, name }, [FILE_MSG.OK]);
  }

  /** 원격 항목 삭제(폴더는 통째로). */
  async remove(dir: string, names: string[]): Promise<void> {
    await this.panelCall({ t: FILE_MSG.DELETE, dir, names }, [FILE_MSG.OK]);
  }

  /**
   * 패널 내려받기 — 고른 항목의 **제안**을 받는다. 받을 곳은 호출측이 정해 `receive`(또는 `decline`)를 불러야 한다.
   * 제안은 에이전트가 자기 id 로 만들어 보내므로(`pull` 과 같은 규칙) 이 요청 id 로는 거절만 온다.
   */
  async get(dir: string, names: string[]): Promise<Offer> {
    this.requireOpen();
    const x = newTransferId();
    const replies = this.listen(x);
    try {
      this.dc.send(JSON.stringify({ t: FILE_MSG.GET, x, dir, names }));
      const v = await this.rejectOrOffer(replies);
      if (v.t === FILE_MSG.REJECT) {
        throw new FileError(String(v.err ?? FILE_ERR.FAILED));
      }
      return parseOffer(v);
    } finally {
      replies.close();
    }
  }

  // ── 내부 ────────────────────────────────────────────────────────────────

  private offerWaiters: ((v: Record<string, unknown>) => void)[] = [];

  /**
   * 이 요청 id 의 **거절**과 에이전트의 **새 제안** 중 먼저 오는 것(pull·dragout·get 공통).
   *
   * 🔴 2026-09-17 결함(원격 x64 끌어내기 실기): 거절이 먼저 오면 제안 대기가 **최대 60초 대기열에 남았다.**
   *    그 사이 다음 요청의 제안이 오면 남은 대기가 먼저 가져가고(아무도 결과를 보지 않는다) **제안이 사라졌다** —
   *    "선택 없음" 직후의 끌어내기, "바뀐 것 없음" 직후의 Ctrl+C 가 조용히 실패했다.
   *    ⇒ 경주가 끝나면 **반드시** 제안 대기를 치운다.
   */
  private async rejectOrOffer(replies: { next: (ms: number, types: string[]) => Promise<Record<string, unknown>> }) {
    const offer = this.nextOffer(PULL_TIMEOUT_MS);
    try {
      return await Promise.race([replies.next(PULL_TIMEOUT_MS, [FILE_MSG.REJECT]), offer.promise]);
    } finally {
      offer.cancel();
    }
  }

  /** 다음 제안 대기. `cancel` = 대기열에서 빼고 타이머를 멈춘다(이미 받았으면 아무 일도 안 한다). */
  private nextOffer(ms: number): { promise: Promise<Record<string, unknown>>; cancel: () => void } {
    let timer = 0;
    let waiter: ((v: Record<string, unknown>) => void) | null = null;
    const remove = () => {
      window.clearTimeout(timer);
      this.offerWaiters = this.offerWaiters.filter((w) => w !== waiter);
    };
    const promise = new Promise<Record<string, unknown>>((resolve, reject) => {
      timer = window.setTimeout(() => {
        remove();
        reject(new FileError(FILE_ERR.TIMEOUT));
      }, ms);
      waiter = (v) => {
        window.clearTimeout(timer);
        resolve(v);
      };
      this.offerWaiters.push(waiter);
    });
    return { promise, cancel: remove };
  }

  private requireOpen(): FileCaps {
    if (!this.caps || this.dc.readyState !== 'open') {
      throw new FileError(FILE_ERR.CLOSED);
    }
    return this.caps;
  }

  private enqueue<T>(fn: () => Promise<T>): Promise<T> {
    const run = this.queue.then(fn, fn);
    this.queue = run.catch(() => undefined);
    return run;
  }

  /** 전송 x 의 JSON 응답을 차례로 꺼내는 창구. */
  private listen(x: number) {
    const inbox: Record<string, unknown>[] = [];
    let wake: (() => void) | null = null;
    let failed: FileError | null = null;
    const listener: Listener = (v) => {
      // 보내는 중에 상대가 취소·실패를 알리면 즉시 멈춘다.
      if (v.t === FILE_MSG.CANCEL) {
        failed = new FileError(FILE_ERR.CANCELED);
      } else if (v.t === FILE_MSG.RESULT && v.ok !== true) {
        failed = new FileError(String(v.err ?? FILE_ERR.FAILED));
      }
      inbox.push(v);
      wake?.();
    };
    this.listeners.set(x, listener);
    const closed = () => this.dc.readyState !== 'open';
    return {
      throwIfFailed: () => {
        if (failed) throw failed;
        if (closed()) throw new FileError(FILE_ERR.CLOSED);
      },
      next: async (ms: number, types: string[]): Promise<Record<string, unknown>> => {
        const deadline = Date.now() + ms;
        for (;;) {
          const idx = inbox.findIndex((v) => types.includes(String(v.t)));
          if (idx >= 0) {
            return inbox.splice(idx, 1)[0];
          }
          if (failed && !types.includes(FILE_MSG.RESULT)) throw failed;
          if (closed()) throw new FileError(FILE_ERR.CLOSED);
          const left = deadline - Date.now();
          if (left <= 0) throw new FileError(FILE_ERR.TIMEOUT);
          await new Promise<void>((resolve) => {
            const timer = window.setTimeout(resolve, Math.min(left, 1000));
            wake = () => {
              window.clearTimeout(timer);
              resolve();
            };
          });
          wake = null;
        }
      },
      close: () => this.listeners.delete(x),
    };
  }

  /** 보내기 대기열이 차 있으면 LOW_WATER 아래로 내려갈 때까지 기다린다. */
  private flow(): Promise<void> {
    if (this.dc.bufferedAmount <= HIGH_WATER) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const done = () => {
        this.dc.removeEventListener('bufferedamountlow', done);
        this.dc.removeEventListener('close', done);
        resolve();
      };
      this.dc.addEventListener('bufferedamountlow', done);
      this.dc.addEventListener('close', done);
    });
  }

  private failAll(): void {
    for (const l of this.listeners.values()) {
      l({ t: FILE_MSG.CANCEL });
    }
    for (const inc of [...this.incoming.values()]) {
      inc.finish(new FileError(FILE_ERR.CLOSED));
    }
  }

  private onMessage(data: unknown): void {
    if (data instanceof ArrayBuffer) {
      this.onFrame(data);
      return;
    }
    if (typeof data !== 'string') {
      return;
    }
    let v: Record<string, unknown>;
    try {
      v = JSON.parse(data) as Record<string, unknown>;
    } catch {
      return;
    }
    if (v.t === FILE_MSG.HELLO) {
      const caps = parseFileHello(v);
      if (caps) {
        this.caps = caps;
        this.onCaps(caps);
      }
      return;
    }
    const x = typeof v.x === 'number' ? v.x : -1;
    if (v.t === FILE_MSG.OFFER) {
      // 에이전트가 보내려는 제안(pull 에 대한 답). 기다리는 쪽이 없으면 거절한다(요청한 적 없는 전송).
      const waiter = this.offerWaiters.shift();
      if (waiter) {
        waiter(v);
      } else if (this.dc.readyState === 'open') {
        this.dc.send(JSON.stringify({ t: FILE_MSG.REJECT, x, err: FILE_ERR.CANCELED }));
      }
      return;
    }
    const inc = this.incoming.get(x);
    if (inc && (v.t === FILE_MSG.FEND || v.t === FILE_MSG.DONE || v.t === FILE_MSG.CANCEL)) {
      this.onIncomingControl(inc, v);
      return;
    }
    this.listeners.get(x)?.(v);
  }

  private onFrame(buf: ArrayBuffer): void {
    const f = decodeFrame(buf);
    if (!f) {
      return;
    }
    const inc = this.incoming.get(f.x);
    if (!inc || inc.failed) {
      return;
    }
    const e = inc.offer.entries[f.i];
    const at = inc.written.get(f.i) ?? 0;
    if (!e || e.dir || f.offset !== at || at + f.payload.length > e.size) {
      this.failIncoming(inc, new FileError(FILE_ERR.PROTOCOL, '조각 위치·크기 어긋남'));
      return;
    }
    inc.written.set(f.i, at + f.payload.length);
    let h = inc.hashers.get(f.i);
    if (!h) {
      h = new Sha256();
      inc.hashers.set(f.i, h);
    }
    h.update(f.payload);
    // payload 는 받은 버퍼의 일부 — 쓰기가 비동기라 복사해 둔다.
    const data = f.payload.slice();
    const end = at + data.length;
    inc.chain = inc.chain.then(async () => {
      if (inc.failed) return;
      await inc.sink.write(f.i, data);
      const before = inc.received;
      inc.received += data.length;
      inc.onProgress?.({ done: inc.received, total: inc.offer.total });
      // 누적 PROGRESS_EVERY_BYTES 마다 "여기까지 받을 곳에 넘겼다" 를 알린다 → 보내는 쪽 받기 창이 앞으로 간다.
      // (파일별이 아니라 누적으로 센다 — 작은 파일 수천 개여도 창이 멈추지 않는다.)
      if (Math.floor(before / PROGRESS_EVERY_BYTES) !== Math.floor(inc.received / PROGRESS_EVERY_BYTES) && this.dc.readyState === 'open') {
        this.dc.send(JSON.stringify({ t: FILE_MSG.PROG, x: inc.offer.x, i: f.i, offset: end }));
      }
    });
    inc.chain.catch((err) => this.failIncoming(inc, err));
  }

  private onIncomingControl(inc: Incoming, v: Record<string, unknown>): void {
    const x = inc.offer.x;
    if (v.t === FILE_MSG.CANCEL) {
      this.failIncoming(inc, new FileError(FILE_ERR.CANCELED), false);
      return;
    }
    if (v.t === FILE_MSG.FEND) {
      const i = typeof v.i === 'number' ? v.i : -1;
      const e = inc.offer.entries[i];
      const got = (inc.hashers.get(i) ?? new Sha256()).digestHex();
      inc.hashers.delete(i);
      if (!e || (inc.written.get(i) ?? 0) !== e.size) {
        this.failIncoming(inc, new FileError(FILE_ERR.PROTOCOL, '크기 불일치'));
        return;
      }
      if (typeof v.sha256 !== 'string' || got !== v.sha256.toLowerCase()) {
        this.failIncoming(inc, new FileError(FILE_ERR.HASH_MISMATCH));
        return;
      }
      inc.chain = inc.chain.then(() => inc.sink.finishFile(i));
      inc.chain.catch((err) => this.failIncoming(inc, err));
      return;
    }
    // done
    inc.chain = inc.chain.then(async () => {
      if (inc.failed) return;
      const missing = inc.offer.entries.some((e) => !e.dir && (inc.written.get(e.i) ?? 0) !== e.size);
      if (missing) throw new FileError(FILE_ERR.PROTOCOL, '덜 받고 끝남');
      await inc.sink.complete();
      if (this.dc.readyState === 'open') {
        this.dc.send(JSON.stringify({ t: FILE_MSG.RESULT, x, ok: true }));
      }
      inc.finish();
    });
    inc.chain.catch((err) => this.failIncoming(inc, err));
  }

  private failIncoming(inc: Incoming, e: unknown, tellPeer = true): void {
    if (inc.failed) {
      return;
    }
    const fe = e instanceof FileError ? e : new FileError(FILE_ERR.LOCAL_WRITE, String(e));
    inc.failed = fe;
    if (tellPeer && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify({ t: FILE_MSG.RESULT, x: inc.offer.x, ok: false, err: fe.code }));
    }
    inc.finish(fe);
  }
}

// 번들러가 쓰지 않는 형식 가져오기를 지우지 않게(타입 전용).
export type { Offer };
